import AccountId from "components/atoms/text/labels/AccountId";
import {
  SubscriptionOrgListColumnType,
  SUBSCRIPTION_STATUS,
} from "api/interfaces/subscription.interface";
import { FLEX } from "styles/flex";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import * as mediaQuery from "components/MediaQuery";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import { PlanModal } from "pages/subscription/SubscriptionStyled";
import ExpiredDate from "../common/ExpiredDate";
import {
  getActivateStatus,
  USAGE_PLAN_SWITCH_WARNING_TEXT,
} from "utils/SubscriptionUtil";
import { IoAlertCircle } from "react-icons/io5";

type Props = {
  row: SubscriptionOrgListColumnType;
  onModal: () => void;
  onConfirm: () => void;
};

export default function SwitchToUsagePlanModal({
  row,
  onModal,
  onConfirm,
}: Props): JSX.Element {
  const activateStatus = row.channelExpire
    ? getActivateStatus({
        activateDate: String(new Date()),
        expireDate: row.channelExpire,
      })
    : undefined;

  // channelExpire 가 오늘 날짜 기준으로 아직 지나지 않은 경우
  const isNotExpiredYet =
    !!row.channelExpire && activateStatus !== SUBSCRIPTION_STATUS.EXPIRED_END;

  return (
    <SmallPopupTemplate
      title="Switch to Usage Plan"
      onModal={onModal}
      className="switch-usage-plan-modal"
      cancel={true}
      confirmLabel="Yes"
      onClick={onConfirm}
    >
      <ModalContents>
        <PlanModal.Title>
          <AccountId accountId={row.accountNumber} level={row.level} />
          <span className="account-name-label">{row.name}</span>
        </PlanModal.Title>
        <InfoRow>
          <InfoCard>
            <label>Channels</label>
            <ChannelCount>{row.registeredChannel} CH</ChannelCount>
          </InfoCard>
          <InfoCard>
            <label>
              {activateStatus === SUBSCRIPTION_STATUS.EXPIRED_END
                ? "Expired"
                : "Expire"}{" "}
              Date
            </label>
            {row.channelExpire ? (
              <ExpiredDate
                status={activateStatus ?? SUBSCRIPTION_STATUS.NONE}
                expiredDate={row.channelExpire}
              />
            ) : (
              <EmptyValue>-</EmptyValue>
            )}
          </InfoCard>
        </InfoRow>

        {isNotExpiredYet && (
          <WarningBox>
            <IoAlertCircle size={16} />
            <span>{USAGE_PLAN_SWITCH_WARNING_TEXT}</span>
          </WarningBox>
        )}

        <Description>
          {isNotExpiredYet ? (
            <>
              Activating this end user on your plan will add{" "}
              {row.registeredChannel} channels of usage.
            </>
          ) : (
            <>
              This organization&apos;s license keys have expired. Enabling
              auto-enroll will immediately place them on your usage plan.
              Activating this end user on your plan will add{" "}
              {row.registeredChannel} channels of usage.
            </>
          )}
          <strong>Would you like to add {row.name} to your Usage plan?</strong>
        </Description>
      </ModalContents>
    </SmallPopupTemplate>
  );
}

const ModalContents = styled.div`
  width: 100%;
  min-width: 460px;
  color: ${({ theme }) => theme.COLORS.WHITE};
  text-align: center;
  ${FLEX.FlexCenterCenter};
  flex-direction: column;

  .account-id-label {
    padding: 7px 12px;
    font-size: ${calcRem(14)};
  }

  .account-name-label {
    font-size: ${calcRem(18)};
  }
  ${mediaQuery.isMobile} {
    min-width: 100%;
  }
`;

const InfoRow = styled.div`
  width: 80%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 18px 0;

  ${mediaQuery.isMobile} {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.COLORS.SELECTED}10;
  border: 1px solid ${({ theme }) => theme.COLORS.SELECTED}30;
  text-align: center;

  label {
    font-size: ${calcRem(12)};
    color: ${({ theme }) => theme.COLORS.WHITE}70;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

const ChannelCount = styled.div`
  font-size: ${calcRem(20)};
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.WHITE};
  line-height: 1.1;
`;

const EmptyValue = styled.div`
  font-size: ${calcRem(20)};
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.WHITE}70;
  line-height: 1.1;
`;

const WarningBox = styled.div`
  width: 100%;
  ${FLEX.FlexStartStart};
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.COLORS.WARNING};
  background-color: ${({ theme }) => theme.COLORS.WARNING}1A;
  color: ${({ theme }) => theme.COLORS.WARNING};
  font-size: ${calcRem(14)};
  line-height: 1.5;
  text-align: left;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Description = styled.p`
  line-height: 1.5;
  font-size: ${calcRem(14)};

  strong {
    font-weight: 600;
    display: block;
    margin-top: 16px;
  }
`;
